import api from '@api'
import {
  buildFlatQualificationStructure,
  buildRelationalQualificationStructure,
} from '@data/qualification'
import {
  getRootTranslator,
  getRootPluaralTranslator,
} from '@utils/get-module-translator'
import {
  transformWarrantySync,
  transformWarrantySyncForServer,
  transformWarrantySyncHistory,
  transformWarrantySyncFailedDevices,
} from '@data/warranty-sync'
import { transformSupportData } from '@data/workflow'
import { successToast, errorToast } from '@motadata/ui'

const __rootT = getRootTranslator()
const __rootTc = getRootPluaralTranslator()

const buildQualifications = (qualificationFactors) => {
  const quals = []
  if (qualificationFactors.name) {
    quals.push(
      buildRelationalQualificationStructure(
        'name',
        'contains',
        qualificationFactors.name,
        'string',
        'db'
      )
    )
  }
  if (qualificationFactors.enabled) {
    quals.push(
      buildRelationalQualificationStructure(
        'disabled',
        'equal',
        false,
        'boolean',
        'db'
      )
    )
  }
  if (qualificationFactors.syncFor) {
    quals.push(
      buildRelationalQualificationStructure(
        'syncFor',
        'in',
        qualificationFactors.syncFor,
        'enum',
        'db'
      )
    )
  }
  if (qualificationFactors.applicableModules) {
    quals.push(
      buildRelationalQualificationStructure(
        'syncFor',
        'in',
        qualificationFactors.applicableModules,
        'enum',
        'db'
      )
    )
  }
  return buildFlatQualificationStructure(quals)
}

export function getWarrantySyncSearchApi(filter, limit, offset) {
  return api
    .post(
      `/warrantysync/search/byqual`,
      {
        qualDetails: buildQualifications(filter),
      },
      {
        params: {
          offset: offset || 0,
          size: limit === undefined ? 1000 : limit,
        },
        notify: false,
      }
    )
    .then((data) => {
      return {
        items: (data.objectList || []).map(transformWarrantySync),
        total: data.totalCount,
      }
    })
}

export function getWarrantySyncApi(id) {
  return api
    .get(`warrantysync/${id}`)
    .then((data) => transformWarrantySync(data))
}

export function createWarrantySyncApi(data) {
  return api
    .post(`/warrantysync`, transformWarrantySyncForServer(data), {
      message: __rootT('created_successfully', {
        resource: __rootTc('warranty_sync'),
      }),
    })
    .then(transformWarrantySync)
}

export function updateWarrantySyncApi(data) {
  return api
    .patch(`/warrantysync/${data.id}`, transformWarrantySyncForServer(data), {
      message: __rootT('updated_successfully', {
        resource: __rootTc('warranty_sync'),
      }),
    })
    .then(transformWarrantySync)
}

export function toggleEnableApi(id, data) {
  return api
    .patch(`/warrantysync/${id}`, data, {
      message: __rootT('updated_successfully', {
        resource: __rootT('warranty_sync'),
      }),
    })
    .then((data) => transformWarrantySync(data))
}

export function deleteWarrantySyncApi({ id }) {
  return api.delete(`/warrantysync/${id}`, {
    message: __rootT('deleted_successfully', {
      resource: __rootTc('warranty_sync'),
    }),
  })
}

export function getSupportDataApi(selectedModule) {
  return api
    .get(`${selectedModule}/warrantysync/supportedData`)
    .then(transformSupportData)
}

export function getWarrantySyncHistoryApi(refId, limit, offset) {
  return api
    .post(
      `/warrantysync/history/search/byqual`,
      {
        qualDetails: buildFlatQualificationStructure([
          buildRelationalQualificationStructure(
            'refId',
            'equal',
            refId,
            'long',
            'db'
          ),
        ]),
      },
      {
        params: {
          offset: offset || 0,
          size: limit === undefined ? 1000 : limit,
          sort_by: 'syncDate',
        },
        notify: false,
      }
    )
    .then((data) => {
      return {
        items: (data.objectList || []).map(transformWarrantySyncHistory),
        total: data.totalCount,
      }
    })
}

export function getWarrantySyncFailedDevicesApi(refId, limit, offset) {
  return api
    .post(
      `/warrantysync/faileddetails/search/byqual`,
      {
        qualDetails: buildFlatQualificationStructure([
          buildRelationalQualificationStructure(
            'refId',
            'equal',
            refId,
            'long',
            'db'
          ),
        ]),
      },
      {
        params: {
          offset: offset || 0,
          size: limit === undefined ? 1000 : limit,
          sort_by: 'createdTime',
        },
        notify: false,
      }
    )
    .then((data) => {
      return {
        items: (data.objectList || []).map(transformWarrantySyncFailedDevices),
        total: data.totalCount,
      }
    })
}

export function testWarrantySyncConnectionApi({
  apiVendor,
  clientId,
  clientSecret,
}) {
  return api
    .post('/warrantysync/testConnection', {
      apiVendor,
      clientId,
      clientSecret,
    })
    .then((res) => {
      if (res && res.connected) {
        successToast(__rootT('warranty_sync_connected_successfully'))
      } else {
        errorToast(res.errorMessage || __rootT('connection_failed'))
      }
      return res
    })
    .catch((e) => {
      errorToast((e && e.message) || __rootT('connection_failed'))
      throw e
    })
}
